import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { formatDateTime } from '../../../../../core/utils/date.util';
import { ChargeHistoryItem, ChargeListingService } from '../../../services/charge-listing.service';

@Component({
  selector: 'app-charge-history-modal',
  standalone: false,
  templateUrl: './charge-history-modal.component.html',
  styleUrl: './charge-history-modal.component.css'
})
export class ChargeHistoryModalComponent implements OnChanges {
  @Input() patientVisitId: number | string | null = null;
  @Input() visitChargeEntryId: number | string | null = null;
  @Input() chargeNo: string = '';
  @Output() close = new EventEmitter<void>();

  activeTab: 'submit' | 'resubmit' | 'editing' = 'submit';
  isLoading = false;
  loadError: string | null = null;
  historyRows: ChargeHistoryItem[] = [];
  submitRows: ChargeHistoryItem[] = [];
  resubmitRows: ChargeHistoryItem[] = [];
  editingRows: ChargeHistoryItem[] = [];
  visibleRows: ChargeHistoryItem[] = [];
  private loadRequestSeq = 0;

  constructor(
    private chargeListingService: ChargeListingService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['patientVisitId'] || changes['visitChargeEntryId']) {
      this.loadHistory();
    }
  }

  onClose(): void {
    this.close.emit();
  }

  setTab(tab: 'submit' | 'resubmit' | 'editing'): void {
    this.activeTab = tab;
    this.visibleRows = this.getRowsForTab(tab);
  }

  formatHistoryDate(value: string | null | undefined): string {
    return value ? formatDateTime(value) || String(value) : '';
  }

  getHistoryType(row: ChargeHistoryItem): string {
    return row.chargeHistorySubmitTypeName || row.chargeHistorySubmitType || row.chargeHistoryChargeStatus || '';
  }

  trackByHistory(_index: number, row: ChargeHistoryItem): number | string {
    return row.chargeHistoryId ?? `${row.chargeHistoryDateTime ?? ''}-${row.chargeHistorySubmitType ?? ''}-${row.visitChargeEntryId ?? ''}`;
  }

  private loadHistory(): void {
    const patientVisitId = this.toPositiveNumber(this.patientVisitId);
    const visitChargeEntryId = this.toPositiveNumber(this.visitChargeEntryId);
    const requestSeq = ++this.loadRequestSeq;

    this.historyRows = [];
    this.submitRows = [];
    this.resubmitRows = [];
    this.editingRows = [];
    this.visibleRows = [];
    this.loadError = null;

    if (patientVisitId == null || visitChargeEntryId == null) {
      this.activeTab = 'submit';
      this.loadError = 'Charge history is unavailable for the selected charge.';
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    this.isLoading = true;
    this.cdr.detectChanges();
    this.chargeListingService.getChargeHistory({ patientVisitId, visitChargeEntryId }).subscribe({
      next: (rows) => {
        if (requestSeq !== this.loadRequestSeq) return;
        try {
          this.historyRows = Array.isArray(rows) ? rows : [];
          this.categorizeRows(this.historyRows);
          this.syncActiveTab();
        } catch (err: any) {
          this.historyRows = [];
          this.submitRows = [];
          this.resubmitRows = [];
          this.editingRows = [];
          this.visibleRows = [];
          this.activeTab = 'submit';
          this.loadError = err?.message || 'Failed to render charge history.';
        } finally {
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      },
      error: (err: any) => {
        if (requestSeq !== this.loadRequestSeq) return;
        this.historyRows = [];
        this.submitRows = [];
        this.resubmitRows = [];
        this.editingRows = [];
        this.visibleRows = [];
        this.activeTab = 'submit';
        this.loadError = err?.error?.message || err?.message || 'Failed to load charge history.';
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private syncActiveTab(): void {
    if (this.getRowsForTab(this.activeTab).length > 0) {
      this.visibleRows = [...this.getRowsForTab(this.activeTab)];
      return;
    }
    if (this.submitRows.length > 0) {
      this.setTab('submit');
      return;
    }
    if (this.resubmitRows.length > 0) {
      this.setTab('resubmit');
      return;
    }
    if (this.editingRows.length > 0) {
      this.setTab('editing');
      return;
    }
    this.setTab('submit');
  }

  private categorizeRows(rows: ChargeHistoryItem[]): void {
    this.submitRows = [];
    this.resubmitRows = [];
    this.editingRows = [];

    for (const row of rows) {
      const tab = this.resolveTab(row);
      if (tab === 'resubmit') {
        this.resubmitRows.push(row);
      } else if (tab === 'editing') {
        this.editingRows.push(row);
      } else {
        this.submitRows.push(row);
      }
    }
  }

  private getRowsForTab(tab: 'submit' | 'resubmit' | 'editing'): ChargeHistoryItem[] {
    if (tab === 'resubmit') return this.resubmitRows;
    if (tab === 'editing') return this.editingRows;
    return this.submitRows;
  }

  private resolveTab(row: ChargeHistoryItem): 'submit' | 'resubmit' | 'editing' {
    const normalized = [
      row.chargeHistorySubmitTypeName,
      row.chargeHistorySubmitType,
      row.chargeHistoryChargeStatus
    ]
      .filter((value) => value != null && String(value).trim() !== '')
      .join(' ')
      .toLowerCase();

    if (normalized.includes('resubmit') || normalized.includes('re-submit') || normalized.includes('re submit')) {
      return 'resubmit';
    }
    if (normalized.includes('edit')) {
      return 'editing';
    }
    return 'submit';
  }

  private toPositiveNumber(value: number | string | null | undefined): number | null {
    if (value == null || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
}

